import { Pcg32 } from '../rng';
import type { Color, MatchConfig, MatchEvent, MatchState, Pawn, Side } from '../types';
import type { AppraisalTarget, HiddenPattern, HouseRuleId, PawnStatusKind } from './ids';
import type {
  HouseRuleSpec,
  ItemSpec,
  KitSpec,
  LedgerRoundState,
  LedgerState,
  SideStatus,
  SideStatusKind,
  StatusInstance,
} from './types';

/**
 * The Long Night ledger state and its helpers. Every update replaces the objects it changes
 * (copy on write), so a cloned MatchState can share the ledger with the state it came from.
 */

export const STREAM_LUCK = 0x4c55434bn; // "LUCK"

const both = <T>(v: () => T): Record<Side, T> => ({ S: v(), N: v() });

export const freshRound = (): LedgerRoundState => ({
  turns: both(() => 0),
  lost: both(() => 0),
  chains: both(() => 0),
  patterns: both(() => []),
  firstColor: both(() => null),
  colors: both(() => []),
  firstPasser: null,
  once: [],
});

/** The ledger a Long Night match starts with; undefined for the rules 1.0 game. */
export function initLedger(config: MatchConfig, seed64: bigint): LedgerState | undefined {
  const l = config.ledger;
  if (!l) return undefined;
  const kits = l.kits;
  return {
    luck: Pcg32.seeded(seed64, STREAM_LUCK).toState(),
    mantel: { S: kits.S.mantel.slice(), N: kits.N.mantel.slice() },
    stolen: [],
    lost: both(() => []),
    curios: { S: kits.S.curios.slice(), N: kits.N.curios.slice() },
    statuses: {},
    sideStatuses: both(() => []),
    omens: l.omens.map((o, i) => ({
      id: i + 1,
      kind: o.kind,
      square: o.square,
      shrouded: !!o.shrouded,
      uses: 0,
      spent: false,
    })),
    nextOmenId: l.omens.length + 1,
    brass: { S: kits.S.brass, N: kits.N.brass },
    earned: both(() => 0),
    capped: both(() => ({})),
    counters: { S: { ...kits.S.counters }, N: { ...kits.N.counters } },
    pawnValues: {},
    cardStamps: {},
    cardValues: {},
    shattered: [],
    facesPlayed: both(() => []),
    triggers: {},
    pileEpitaphs: {},
    skipTurns: both(() => 0),
    round: freshRound(),
  };
}

/** The ledger of a Long Night state; throws when there is none (a programming error). */
export function L(s: Pick<MatchState, 'ledger'>): LedgerState {
  if (!s.ledger) throw new Error('not a Long Night match');
  return s.ledger;
}

export const isLedger = (s: Pick<MatchState, 'ledger'>): boolean => s.ledger !== undefined;

export const kitOf = (s: Pick<MatchState, 'config'>, side: Side): KitSpec | undefined =>
  s.config.ledger?.kits[side];

/** Superstitions currently on the side's Mantel, leftmost first. */
export const mantelOf = (s: Pick<MatchState, 'ledger'>, side: Side): readonly ItemSpec[] =>
  s.ledger?.mantel[side] ?? [];

export const holds = (s: Pick<MatchState, 'ledger'>, side: Side, id: string): boolean =>
  mantelOf(s, side).some((i) => i.id === id);

export const holdCount = (s: Pick<MatchState, 'ledger'>, side: Side, id: string): number =>
  mantelOf(s, side).filter((i) => i.id === id).length;

/** The House Rule played against `side` (or benefiting it, for 'warded-start'), if any. */
export function houseRule(
  s: Pick<MatchState, 'config'>,
  id: HouseRuleId,
  side?: Side,
): HouseRuleSpec | undefined {
  return s.config.ledger?.houseRules.find(
    (r) => r.id === id && (side === undefined || (r.side ?? 'S') === side),
  );
}

export const hasRule = (s: Pick<MatchState, 'config'>, id: HouseRuleId, side?: Side): boolean =>
  houseRule(s, id, side) !== undefined;

export function appraisal(
  s: Pick<MatchState, 'config'>,
  side: Side,
  target: AppraisalTarget,
): number {
  return Math.max(1, kitOf(s, side)?.appraisals[target] ?? 1);
}

/** Hidden patterns that count for the side: discovered, or named by a Superstition it holds. */
export function discovered(s: Pick<MatchState, 'config' | 'ledger'>, side: Side): HiddenPattern[] {
  const out = new Set<HiddenPattern>(kitOf(s, side)?.discovered ?? []);
  if (holds(s, side, 'stairwell')) out.add('stair');
  if (holds(s, side, 'prism')) out.add('prism');
  if (holds(s, side, 'palindrome')) out.add('mirror');
  return [...out];
}

// ---------------------------------------------------------------------------------------------
// Limits that items and House Rules change
// ---------------------------------------------------------------------------------------------

export function maxLinks(s: Pick<MatchState, 'config' | 'ledger'>, side: Side): number {
  const base = s.config.constants.MAX_LINKS;
  if (!s.ledger) return base;
  const n = base + (holds(s, side, 'chain-letter') ? 1 : 0);
  // Gag (a Trick): the side's next chain has only 1 link.
  if (s.ledger.sideStatuses[side].some((x) => x.kind === 'gagged')) return 1;
  return hasRule(s, 'heavy-chains', side) ? Math.min(2, n) : n;
}

export function epitaphSlots(s: Pick<MatchState, 'config' | 'ledger'>, side: Side): number {
  const base = s.config.constants.EPITAPH_SLOTS;
  if (!s.ledger) return base;
  return base + holdCount(s, side, 'widows-veil') + (kitOf(s, side)?.bonuses.epitaphSlots ?? 0);
}

export function epitaphPlays(s: Pick<MatchState, 'config' | 'ledger'>, side: Side): number {
  const base = s.config.constants.EPITAPH_PLAYS_PER_ROUND;
  if (!s.ledger) return base;
  return base + (kitOf(s, side)?.bonuses.epitaphPlays ?? 0);
}

/** The hand limit (§22), plus Fixtures and Ghost cards in the deck, minus Short Hand. */
export function handLimit(s: Pick<MatchState, 'config' | 'ledger'>, side: Side): number {
  const base = s.config.constants.HAND_LIMIT;
  if (!s.ledger) return base;
  const ghosts = (s.config.decks?.[side] ?? []).filter((c) => c.finish === 'ghost').length;
  const short = hasRule(s, 'short-hand', side) ? 1 : 0;
  return Math.max(1, base + (kitOf(s, side)?.bonuses.handLimit ?? 0) + ghosts - short);
}

/**
 * A House Rule's number when its spec names none: the one the rule plays with, and the one its
 * text shows (Short Wick's turns, Pickpocket's and Toll's brass, the Clock's turns).
 */
export const RULE_N: Partial<Record<HouseRuleId, number>> = {
  'short-wick': 5,
  pickpocket: 2,
  toll: 1,
  clock: 6,
};

/** A House Rule's number: its own, else the rule's default. */
export const ruleN = (r: Pick<HouseRuleSpec, 'id' | 'n'>): number | undefined =>
  r.n ?? RULE_N[r.id];

/**
 * The rule that limits a side's turns in a round (Short Wick, Clock), and its number; null when
 * the side may take as many turns as it likes.
 */
export function turnLimitRule(
  s: Pick<MatchState, 'config'>,
  side: Side,
): { readonly rule: 'short-wick' | 'clock'; readonly n: number } | null {
  const wick = houseRule(s, 'short-wick', side);
  const clock = s.config.ledger?.houseRules.find((r) => r.id === 'clock');
  const w = wick ? (ruleN(wick) ?? 5) : Infinity;
  const c = clock ? (ruleN(clock) ?? 6) : Infinity;
  if (w === Infinity && c === Infinity) return null;
  return w <= c ? { rule: 'short-wick', n: w } : { rule: 'clock', n: c };
}

/** Turns a side may take in a round (Short Wick, Clock); Infinity when unlimited. */
export function turnLimit(s: Pick<MatchState, 'config'>, side: Side): number {
  return turnLimitRule(s, side)?.n ?? Infinity;
}

// ---------------------------------------------------------------------------------------------
// Statuses
// ---------------------------------------------------------------------------------------------

export const statusesOf = (
  s: Pick<MatchState, 'ledger'>,
  pawnId: string,
): readonly StatusInstance[] => s.ledger?.statuses[pawnId] ?? [];

export const statusOf = (
  s: Pick<MatchState, 'ledger'>,
  pawnId: string,
  kind: PawnStatusKind,
): StatusInstance | undefined => statusesOf(s, pawnId).find((x) => x.kind === kind);

export const hasStatus = (
  s: Pick<MatchState, 'ledger'>,
  pawnId: string,
  kind: PawnStatusKind,
): boolean => statusOf(s, pawnId, kind) !== undefined;

/** Turn ends of its owner a status lasts to cover "until the end of its owner's next turn". */
export const untilOwnersNextTurn = (s: Pick<MatchState, 'active'>, owner: Side): number =>
  s.active === owner ? 2 : 1;

export function setStatus(
  s: MatchState,
  pawnId: string,
  status: StatusInstance,
  ev: MatchEvent[],
): void {
  const l = L(s);
  const list = (l.statuses[pawnId] ?? []).filter((x) => x.kind !== status.kind);
  s.ledger = { ...l, statuses: { ...l.statuses, [pawnId]: [...list, status] } };
  ev.push({
    type: 'StatusChanged',
    pawnId,
    status: status.kind,
    change: 'added',
    ...(status.n !== undefined ? { n: status.n } : {}),
  });
}

export function clearStatus(
  s: MatchState,
  pawnId: string,
  kind: PawnStatusKind,
  ev: MatchEvent[],
): void {
  const l = L(s);
  const list = l.statuses[pawnId];
  if (!list || !list.some((x) => x.kind === kind)) return;
  const rest = list.filter((x) => x.kind !== kind);
  const statuses = { ...l.statuses };
  if (rest.length) statuses[pawnId] = rest;
  else delete statuses[pawnId];
  s.ledger = { ...l, statuses };
  ev.push({ type: 'StatusChanged', pawnId, status: kind, change: 'removed' });
}

export const sideStatus = (
  s: Pick<MatchState, 'ledger'>,
  side: Side,
  kind: SideStatusKind,
): SideStatus | undefined => s.ledger?.sideStatuses[side].find((x) => x.kind === kind);

export function addSideStatus(s: MatchState, side: Side, kind: SideStatusKind, n: number): void {
  const l = L(s);
  const list = l.sideStatuses[side];
  const prior = list.find((x) => x.kind === kind);
  const next = prior
    ? list.map((x) => (x.kind === kind ? { kind, n: x.n + n } : x))
    : [...list, { kind, n }];
  s.ledger = { ...l, sideStatuses: { ...l.sideStatuses, [side]: next } };
}

/** Uses one of a side status's charges; returns whether there was one. */
export function useSideStatus(s: MatchState, side: Side, kind: SideStatusKind): boolean {
  const l = L(s);
  const prior = l.sideStatuses[side].find((x) => x.kind === kind);
  if (!prior || prior.n <= 0) return false;
  const next = l.sideStatuses[side]
    .map((x) => (x.kind === kind ? { kind, n: x.n - 1 } : x))
    .filter((x) => x.n > 0);
  s.ledger = { ...l, sideStatuses: { ...l.sideStatuses, [side]: next } };
  return true;
}

// ---------------------------------------------------------------------------------------------
// Brass, counters and round bookkeeping
// ---------------------------------------------------------------------------------------------

/** Changes a side's brass (never below 0 here: debt is the campaign's business). */
export function addBrass(
  s: MatchState,
  side: Side,
  amount: number,
  reason: string,
  ev: MatchEvent[],
  cap?: { key: string; max: number },
): number {
  const l = L(s);
  let a = amount;
  let capped = l.capped;
  if (cap && a > 0) {
    const so = l.capped[side][cap.key] ?? 0;
    a = Math.max(0, Math.min(a, cap.max - so));
    if (a === 0) return 0;
    capped = { ...l.capped, [side]: { ...l.capped[side], [cap.key]: so + a } };
  }
  if (a < 0) a = -Math.min(-a, Math.max(0, l.brass[side]));
  if (a === 0) return 0;
  const brass = l.brass[side] + a;
  s.ledger = {
    ...l,
    capped,
    brass: { ...l.brass, [side]: brass },
    earned: { ...l.earned, [side]: l.earned[side] + a },
  };
  ev.push({ type: 'BrassChanged', side, amount: a, reason, brass });
  return a;
}

export const counter = (s: Pick<MatchState, 'ledger'>, side: Side, key: string): number =>
  s.ledger?.counters[side][key] ?? 0;

export function bumpCounter(s: MatchState, side: Side, key: string, by = 1): void {
  const l = L(s);
  s.ledger = {
    ...l,
    counters: {
      ...l.counters,
      [side]: { ...l.counters[side], [key]: (l.counters[side][key] ?? 0) + by },
    },
  };
}

export function updateRound(s: MatchState, fn: (r: LedgerRoundState) => LedgerRoundState): void {
  const l = L(s);
  s.ledger = { ...l, round: fn(l.round) };
}

export const onceUsed = (s: Pick<MatchState, 'ledger'>, key: string): boolean =>
  s.ledger?.round.once.includes(key) ?? false;

export function markOnce(s: MatchState, key: string): void {
  updateRound(s, (r) => ({ ...r, once: [...r.once, key] }));
}

export function bumpSide<T extends 'turns' | 'lost' | 'chains'>(
  s: MatchState,
  key: T,
  side: Side,
  by = 1,
): void {
  updateRound(s, (r) => ({ ...r, [key]: { ...r[key], [side]: r[key][side] + by } }));
}

export function noteColor(s: MatchState, side: Side, color: Color): void {
  updateRound(s, (r) => ({ ...r, colors: { ...r.colors, [side]: [...r.colors[side], color] } }));
}

/** Records that an item triggered (Collateral Call takes the most-triggered one). */
export function noteTrigger(s: MatchState, uid: string): void {
  const l = L(s);
  s.ledger = { ...l, triggers: { ...l.triggers, [uid]: (l.triggers[uid] ?? 0) + 1 } };
}

/** Replaces one Mantel item of a side (a growing value). */
export function replaceItem(s: MatchState, side: Side, uid: string, item: ItemSpec): void {
  const l = L(s);
  s.ledger = {
    ...l,
    mantel: { ...l.mantel, [side]: l.mantel[side].map((x) => (x.uid === uid ? item : x)) },
  };
}

/** Value of a growing Carving on a pawn: the match's value, else the one it came with. */
export function carvingValue(s: Pick<MatchState, 'ledger'>, pawn: Pawn, id: string): number {
  const v = s.ledger?.pawnValues[`${pawn.id}:${id}`];
  if (v !== undefined) return v;
  return pawn.carvings?.find((c) => c.id === id)?.value ?? 0;
}

export const hasCarving = (pawn: Pawn | undefined, id: string): boolean =>
  !!pawn?.carvings?.some((c) => c.id === id);
